import { getAccessToken } from './getAccessToken';
import moment from 'moment';

const environmentUrl = "https://orgea61b289.crm8.dynamics.com";

function normalizeGuid(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  return String(value).replace(/[{}]/g, '');
}

export interface ProofingRecordData {
  cr3ea_qualitytourid: string;
  cr3ea_title: string;
  cr3ea_cycle: string;
  cr3ea_shift: string | null;
  cr3ea_tourstartdate: string;
  cr3ea_observedtime: string;
  cr3ea_standard_proofing_time: string | null;
  cr3ea_observedby: string | null;
  cr3ea_productname: string;
  cr3ea_executivename: string;
  cr3ea_linenumber: string | null;
  cr3ea_variety: string | null;
  /** Proofing – In Time */
  cr3ea_proofingtime: string | null;
  /** Proofing – Out Time */
  cr3ea_proofing_out_time: string | null;
  cr3ea_proofertemp: string | null;
  cr3ea_rhproofer: string | null;
  cr3ea_platform: string;
}

export interface SaveResponse {
  success: boolean;
  message: string;
  data?: any;
}

function getShiftValue(): string {
  if (typeof sessionStorage === 'undefined') return 'shift 1';
  return sessionStorage.getItem('shiftValue') || 'shift 1';
}

export function collectProofingRecord(
  cycleNum: number,
  product: string,
  executiveName: string,
  lineNumber: string | null,
  observedDateTime: string,
  standardProofingTime: string | null,
  variety: string | null,
  proofingInTime: string | null,
  proofingOutTime: string | null,
  prooferTemp: string | null,
  rhProofer: string | null,
  QualityTourId: string,
  UserName: string | null
): ProofingRecordData {
  return {
    cr3ea_qualitytourid: normalizeGuid(QualityTourId),
    cr3ea_title: 'Proofing_' + moment().format('MM-DD-YYYY'),
    cr3ea_cycle: `Cycle-${cycleNum}`,
    cr3ea_shift: getShiftValue(),
    cr3ea_tourstartdate: moment().format('MM-DD-YYYY'),
    cr3ea_observedtime: observedDateTime || moment().format('MM-DD-YYYY hh:mm:ss A'),
    cr3ea_standard_proofing_time: standardProofingTime || null,
    cr3ea_observedby: UserName || null,
    cr3ea_productname: product || 'N/A',
    cr3ea_executivename: executiveName || 'N/A',
    cr3ea_linenumber: lineNumber || null,
    cr3ea_variety: variety || null,
    cr3ea_proofingtime: proofingInTime || null,
    cr3ea_proofing_out_time: proofingOutTime || null,
    cr3ea_proofertemp: prooferTemp || null,
    cr3ea_rhproofer: rhProofer || null,
    cr3ea_platform: 'PWA',
  };
}

export async function savesectionApicall(
  data: ProofingRecordData[],
  optionalAccessToken?: string
): Promise<SaveResponse> {
  try {
    const buttons = document.querySelectorAll("button");
    buttons.forEach(button => (button as HTMLButtonElement).disabled = true);

    let accessToken: string | undefined = optionalAccessToken;
    if (!accessToken) {
      const tokenResult = await getAccessToken();
      accessToken = tokenResult?.token;
    }
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "Content-Type": "application/json; charset=utf-8",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Prefer": "return=representation",
      "Authorization": `Bearer ${accessToken}`,
    };

    const apiVersion = "9.2";
    const tableName = "cr3ea_prod_proofings";
    const apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}`;

    const results: any[] = [];
    for (const record of data) {
      let response = await fetch(apiUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(record),
      });

      if (response.status === 401) {
        // Retry once with a fresh token (e.g. sync runs long and token may have expired)
        localStorage.removeItem("access_token");
        const retryToken = await getAccessToken(undefined, undefined, true);
        const retryAccessToken = (retryToken?.token || "").toString().trim();
        if (retryAccessToken) {
          const retryHeaders = {
            "Accept": "application/json",
            "Content-Type": "application/json; charset=utf-8",
            "OData-MaxVersion": "4.0",
            "OData-Version": "4.0",
            "Prefer": "return=representation",
            "Authorization": `Bearer ${retryAccessToken}`,
          };
          response = await fetch(apiUrl, {
            method: "POST",
            headers: retryHeaders,
            body: JSON.stringify(record),
          });
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save record: ${response.status} - ${errorText}`);
      }
      const result = await response.json();
      results.push(result);
    }

    buttons.forEach(button => (button as HTMLButtonElement).disabled = false);
    return {
      success: true,
      message: `${data.length} records saved successfully`,
      data: results,
    };
  } catch (error) {
    console.error("Error saving Proofing records:", error);
    const buttons = document.querySelectorAll("button");
    buttons.forEach(button => (button as HTMLButtonElement).disabled = false);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Failed to save records. Please try again.",
      data: [],
    };
  }
}

export interface ProofingCycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  lineNumber: string;
  observedtime: string;
  standardProofingTime: string;
  rows: { variety: string; proofingInTime: string; proofingOutTime: string; prooferTemp: string; rhProofer: string }[];
}

export async function fetchCycleData(
  QualityTourId: string,
  optionalAccessToken?: string | null
): Promise<ProofingCycleData[]> {
  try {
    let accessToken: string | undefined = optionalAccessToken ?? undefined;
    if (!accessToken) {
      const tokenResult = await getAccessToken();
      accessToken = tokenResult?.token;
    }
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Authorization": `Bearer ${accessToken}`,
    };

    const apiVersion = "9.2";
    const tableName = "cr3ea_prod_proofings";
    const qualityTourGuid = normalizeGuid(QualityTourId);

    const selectFields = [
      "cr3ea_cycle",
      "cr3ea_productname",
      "cr3ea_executivename",
      "cr3ea_linenumber",
      "cr3ea_observedtime",
      "cr3ea_standard_proofing_time",
      "cr3ea_variety",
      "cr3ea_proofingtime",
      "cr3ea_proofing_out_time",
      "cr3ea_proofertemp",
      "cr3ea_rhproofer",
    ].join(",");

    const buildUrl = (filter: string) =>
      `${environmentUrl}/api/data/v${apiVersion}/${tableName}` +
      `?$filter=${encodeURIComponent(filter)}` +
      `&$select=${selectFields}`;

    let filter = `cr3ea_qualitytourid eq '${qualityTourGuid}'`;
    let response = await fetch(buildUrl(filter), { headers });

    if (!response.ok) {
      const errText = await response.text();
      if (errText.includes("Unrecognized") || errText.includes("Edm.") || errText.includes("$filter")) {
        filter = `_cr3ea_qualitytourid_value eq '${qualityTourGuid}'`;
        response = await fetch(buildUrl(filter), { headers });
        if (!response.ok) {
          throw new Error(`Failed to fetch data (retry): ${response.status} - ${await response.text()}`);
        }
      } else {
        throw new Error(`Failed to fetch data: ${response.status} - ${errText}`);
      }
    }

    const data = await response.json();
    const byKey: Record<string, ProofingCycleData> = {};

    (data.value || []).forEach((record: any) => {
      const cycleNum = (record.cr3ea_cycle || "").replace("Cycle-", "").trim();
      if (!cycleNum) return;

      const key = `${cycleNum}|${record?.cr3ea_productname ?? ''}|${record?.cr3ea_executivename ?? ''}|${record?.cr3ea_linenumber ?? ''}|${record?.cr3ea_observedtime ?? ''}|${record?.cr3ea_standard_proofing_time ?? ''}`;
      if (!byKey[key]) {
        byKey[key] = {
          cycleNum,
          product: record?.cr3ea_productname ?? "",
          executiveName: record?.cr3ea_executivename ?? "",
          lineNumber: record?.cr3ea_linenumber ?? "",
          observedtime: record?.cr3ea_observedtime ?? "",
          standardProofingTime: record?.cr3ea_standard_proofing_time ?? "",
          rows: [],
        };
      }
      byKey[key].rows.push({
        variety: record?.cr3ea_variety ?? "",
        proofingInTime: record?.cr3ea_proofingtime ?? "",
        proofingOutTime: record?.cr3ea_proofing_out_time ?? "",
        prooferTemp: record?.cr3ea_proofertemp ?? "",
        rhProofer: record?.cr3ea_rhproofer ?? "",
      });
    });

    return Object.values(byKey);
  } catch (error) {
    console.error("Error fetching Proofing cycle data:", error);
    return [];
  }
}
